import type {
  ICheck,
  INamed,
  IOption,
  IOverview,
  IPairDetail,
  IPositionDetail,
  IStep,
  RawCheck,
  RawNamed,
  RawOptionPage,
  RawOverview,
  RawPairDetail,
  RawPositionDetail,
  RawStep,
} from './types';

const toNamed = (raw: RawNamed | null | undefined): INamed => ({ id: raw?.id ?? null, name: raw?.name ?? null });

const toCheck = (raw: RawCheck): ICheck => ({
  id: raw.id,
  group: raw.group,
  status: raw.status,
  value: raw.value ?? '',
  fixTarget: raw.fix_target ?? null,
});

const toSteps = (raw: RawStep[] | null | undefined): IStep[] =>
  (raw ?? []).map(step => ({ id: step.id, count: step.count }));

export const toOptions = (raw: RawOptionPage['records']): IOption[] =>
  (raw ?? []).map(record => ({ id: record.id, name: record.name }));

export const toOverview = (raw: RawOverview): IOverview => ({
  context: {
    unit: raw.context?.unit ?? '',
    sector: raw.context?.sector ?? null,
    sectorId: raw.context?.sector_id ?? null,
    districtId: raw.context?.district_id ?? null,
    legalEntity: raw.context?.legal_entity ?? null,
    activeContracts: raw.context?.active_contracts ?? 0,
    draftContracts: raw.context?.draft_contracts ?? 0,
    engineEnabled: raw.context?.engine_enabled ?? false,
    rateEngine: raw.context?.rate_engine ?? null,
    engineVersion: raw.context?.engine_version ?? null,
    rateCard: raw.context?.rate_card ?? null,
  },
  steps: toSteps(raw.steps),
  rows: (raw.rows ?? []).map(row => ({
    positionId: row.position?.id ?? '',
    positionName: row.position?.name ?? '',
    stage: row.stage,
    shown: row.shown,
    cheapestPayRate: row.cheapest_pay_rate ?? null,
    reached: row.reached,
    agenciesTotal: row.agencies_total,
    outcomeCounts: row.outcome_counts ?? {},
    mainBlocker: row.main_blocker ?? null,
    status: row.status,
    stale: row.stale,
    compareStatus: row.compare_status ?? null,
  })),
  stale: { pairs: raw.stale?.pairs ?? 0, positions: raw.stale?.positions ?? 0 },
  timeline: (raw.timeline ?? []).map(event => ({
    date: event.date,
    amendments: event.amendments ?? 0,
    rateCards: event.rate_cards ?? [],
  })),
  compare: raw.compare ? { date: raw.compare.date, steps: toSteps(raw.compare.steps) } : null,
});

export const toPositionDetail = (raw: RawPositionDetail): IPositionDetail => ({
  positionId: raw.position?.id ?? '',
  positionName: raw.position?.name ?? '',
  status: raw.status,
  stage: raw.stage,
  cheapestPayRate: raw.cheapest_pay_rate ?? null,
  reached: raw.reached,
  agenciesTotal: raw.agencies_total,
  checks: (raw.checks ?? []).map(toCheck),
  groups: (raw.groups ?? []).map(group => ({
    reason: group.reason,
    agencies: (group.agencies ?? []).map(entry => ({
      agencyContractId: entry.agency_contract_id,
      agency: toNamed(entry.agency),
      legalEntity: toNamed(entry.legal_entity),
      payRate: entry.pay_rate ?? null,
    })),
  })),
  winner: raw.winner
    ? {
        agencies: (raw.winner.agencies ?? []).map(toNamed),
        payRate: raw.winner.pay_rate ?? null,
        reaches: !!raw.winner.reaches,
        reason: raw.winner.reason,
      }
    : null,
  comingUp: (raw.coming_up ?? []).map(entry => ({
    date: entry.date,
    agencies: (entry.agencies ?? []).map(toNamed),
  })),
  scopedContractId: raw.scoped_contract_id ?? null,
  scopedContractPending: !!raw.scoped_contract_pending,
  missingRateCardId: raw.missing_rate_card_id ?? null,
});

export const toPairDetail = (raw: RawPairDetail): IPairDetail => ({
  positionId: raw.position_id,
  agencyContractId: raw.agency_contract_id,
  agency: toNamed(raw.agency),
  legalEntity: toNamed(raw.legal_entity),
  reason: raw.reason,
  bookingFormOk: !!raw.booking_form_ok,
  reaches: !!raw.reaches,
  stale: !!raw.stale,
  cheapest: !!raw.cheapest,
  payRate: raw.pay_rate ?? null,
  billRate: raw.bill_rate ?? null,
  billFound: !!raw.bill_found,
  engineVersion: raw.engine_version
    ? {
        id: raw.engine_version.id,
        engine: raw.engine_version.engine ?? null,
        versionNumber: raw.engine_version.version_number ?? null,
      }
    : null,
  rateCard: raw.rate_card
    ? { id: raw.rate_card.id, name: raw.rate_card.name, effectiveFrom: raw.rate_card.effective_from ?? null }
    : null,
  nextAmendmentStartsOn: raw.next_amendment_starts_on ?? null,
  checks: (raw.checks ?? []).map(toCheck),
});
